import { Component, OnInit, OnDestroy } from "@angular/core";
import { CommonModule } from "@angular/common";
import { ActivatedRoute } from "@angular/router";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { PolicyTrackerService } from "./policy-tracker.service";
import { PolicyTrackerData } from "./policy-tracker.interfaces";
import { PolicyTrackerHeaderComponent } from "./policy-tracker-header/policy-tracker-header.component";
import { PolicyTrackerTimelineComponent } from "./policy-tracker-timeline/policy-tracker-timeline.component";
import { PolicyTrackerInfoComponent } from "./policy-tracker-info/policy-tracker-info.component";
import { MatCardModule } from "@angular/material/card";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatChipsModule } from "@angular/material/chips";
import { MatIconModule } from "@angular/material/icon";
import { MatDividerModule } from "@angular/material/divider";
import { FlexLayoutModule } from "@angular/flex-layout";
import { Status, StatusText, StatusClass } from "./policy-status.constants";

@Component({
  selector: "app-policy-tracker",
  templateUrl: "./policy-tracker.component.html",
  styleUrls: ["./policy-tracker.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatProgressBarModule,
    MatChipsModule,
    MatIconModule,
    MatDividerModule,
    FlexLayoutModule,
    PolicyTrackerHeaderComponent,
    PolicyTrackerTimelineComponent,
    PolicyTrackerInfoComponent,
  ],
})
export class PolicyTrackerComponent implements OnInit, OnDestroy {
  private policySubject = new BehaviorSubject<PolicyTrackerData | null>(null);
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private errorSubject = new BehaviorSubject<string | null>(null);
  private destroy$ = new Subject<void>();

  policy$: Observable<PolicyTrackerData | null> = this.policySubject.asObservable();
  loading$: Observable<boolean> = this.loadingSubject.asObservable();
  error$: Observable<string | null> = this.errorSubject.asObservable();

  constructor(private route: ActivatedRoute, private policyTrackerService: PolicyTrackerService) {}

  ngOnInit(): void {
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const token = params["token"];
      if (token) {
        this.loadPolicyStatus(token);
      } else {
        this.errorSubject.next("No token provided");
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadPolicyStatus(token: string): void {
    this.loadingSubject.next(true);
    this.errorSubject.next(null);

    this.policyTrackerService
      .getPolicyStatus(token)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (policy) => {
          this.policySubject.next(policy);
          this.loadingSubject.next(false);
        },
        error: (error) => {
          this.errorSubject.next(error.message || "Failed to load policy status");
          this.loadingSubject.next(false);
        },
      });
  }

  onStatusChange(status: string): void {
    //todo Status changed
  }

  getStatusText(status: string): string {
    return StatusText[status as Status] ?? status.charAt(0).toUpperCase() + status.slice(1);
  }

  getStatusClass(status: string): string {
    return StatusClass[status as Status] ?? "status-submitted";
  }
}
